"use client";

import { Suspense, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { type TeamId } from "@/constants/type";
import { useParams, useSearchParams } from "next/navigation";
import SetSearchParam from "../link/set-search-param";

type Player = {
  id: number;
  official_name: string;
  team_id: TeamId;
  num: string;
  color: string;
}[];

type Props = {
  defaultParam?: { [key: string]: string };
  searchName?: { [key: string]: string };
  queryKeys: string[];
  placeholder: string;
  paramName: string;
};
function debounce(func: Function, delay: number) {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}
function getParams(keys: string[], searchParams: URLSearchParams) {
  return keys.reduce((acc, key) => {
    acc[key] = searchParams.get(key);
    return acc;
  }, {} as Record<string, string | null>);
}

function Filter({ defaultParam = {}, searchName = {}, queryKeys, placeholder, paramName }: Props) {
  const [isPending, startTransition] = useTransition();
  const [isFocused, setIsFocused] = useState(false);
  const [name, setName] = useState("");
  const [isContentMouseOver, setIsContentMouseOver] = useState(false);
  const isFirstRender = useRef(true);

  const [posts, setPosts] = useState<Player | null>(null);
  const { year } = useParams();
  const searchParams = useSearchParams();

  const queryString = useMemo(() => {
    const query = getParams(queryKeys, searchParams);
    return JSON.stringify(query);
  }, [queryKeys, searchParams]);

  const paramString = useMemo(() => {
    const defParam = new URLSearchParams({ ...defaultParam, year: String(year) });
    if (name) defParam.set("name", name);
    for (const [newKey, originalKey] of Object.entries(searchName)) {
      const value = searchParams.get(originalKey);
      if (value !== null) {
        defParam.set(newKey, value);
      }
    }
    return defParam.toString();
  }, [defaultParam, searchName, searchParams, name, year]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    async function fetchPosts() {
      const res = await fetch(`/api/get/player?${paramString}`);
      const data = await res.json();
      setPosts(data);
    }
    startTransition(() => fetchPosts());
  }, [queryString, name, paramString]);

  const handleInPutSetSearchParam = debounce((e: React.ChangeEvent<HTMLInputElement>, name: string) => {
    setName(e.target.value);
  }, 500);

  return (
    <>
      <input
        type="search"
        placeholder={placeholder}
        className={`input input-secondary input-sm ${isPending && "bg-gray-500/80"}`}
        onChange={(e) => handleInPutSetSearchParam(e)}
        onFocus={() => setIsFocused(true)}
        onBlur={debounce(() => setIsFocused(false), 300)}
      />
      {posts && (isFocused || isContentMouseOver) && (
        <div onMouseEnter={() => setIsContentMouseOver(true)} onMouseLeave={() => setIsContentMouseOver(false)}>
          <div className="fixed h-64 w-48 border border-secondary rounded-box bg-base-200 z-50 overflow-y-auto">
            <ul className="menu menu-xs">
              {posts.map((m) => {
                return (
                  <li key={m.id}>
                    <SetSearchParam paramName={paramName} paramVal={String(m.id)}>
                      <div style={{ textDecorationColor: m.color }} id={m.team_id} className="underline decoration-4">
                        #{m.num} {m.official_name}
                      </div>
                    </SetSearchParam>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

export function PlayersNameFilter(props: Props) {
  return (
    <Suspense>
      <Filter {...props} />
    </Suspense>
  );
}
